import addToCart from '../lib/addToCart';
import renderMinicart from '../lib/renderMinicart';
import formatMoney from '../utils/formatMoney';
import { BREAKPOINTS, CLASSES, MOBILE_VARIANT_PICKER, IMAGES } from '../utils/constants';
import { lockBodyScroll, unlockBodyScroll } from '../utils/bodyScrollLock';
import { normalizeImageSrc, getSizedImageSrc, getUniqueImages } from '../utils/shopifyImage';
import trapFocus from '../utils/trapFocus';

const initMobileVariantPicker = () => {
  const pickerEl = document.querySelector('[data-mobile-variant-picker]');
  if (!pickerEl) return;

  const panelEl = pickerEl.querySelector('[data-mobile-variant-panel]');
  const backdropEl = pickerEl.querySelector('[data-mobile-variant-backdrop]');
  const imagesWrapEl = pickerEl.querySelector('[data-mobile-variant-images-wrap]');
  const optionsWrapEl = pickerEl.querySelector('[data-mobile-variant-options-wrap]');
  const titleEl = pickerEl.querySelector('[data-mobile-variant-title]');
  const optionNameEl = pickerEl.querySelector('[data-mobile-variant-option-name]');
  const priceEl = pickerEl.querySelector('[data-mobile-variant-price]');
  const comparePriceEl = pickerEl.querySelector('[data-mobile-variant-compare-price]');
  const saleBadgeEl = pickerEl.querySelector('[data-mobile-variant-sale-badge]');
  const addBtnEl = pickerEl.querySelector('[data-mobile-variant-add]');

  const swatchTemplate = document.querySelector('[data-mobile-variant-swatch-template]');
  const slideTemplate = document.querySelector('[data-mobile-variant-slide-template]');

  if (!panelEl || !swatchTemplate || !slideTemplate) return;

  const state = {
    form: null,
    variants: [],
    selectedVariantId: 0,
    meta: {},
    previouslyFocusedEl: null
  };

  const getSelectedVariant = () => state.variants.find(v => v.id === state.selectedVariantId);

  const getVariantImage = (variant = {}) => {
    const featuredImageSrc = variant.featured_image?.src;
    const featuredMediaSrc = variant.featured_media?.preview_image?.src;
    return normalizeImageSrc(featuredImageSrc || featuredMediaSrc || '');
  };

  const getVariantsFromForm = (form) => {
    const variantsJsonEl = form.querySelector('[data-mobile-variant-options]');
    if (!variantsJsonEl) return [];

    try {
      const variantsRaw = JSON.parse(variantsJsonEl.textContent || '[]');
      if (!Array.isArray(variantsRaw)) return [];

      const select = form.elements?.variant;
      const variantOptions = select ? [...select.options] : [];
      const labelById = new Map(
        variantOptions.map(option => [Number(option.value), option.textContent.trim()])
      );

      return variantsRaw.map(variant => ({
        id: Number(variant.id) || 0,
        title: labelById.get(Number(variant.id)) || variant.title || '',
        available: Boolean(variant.available),
        price: Number(variant.price) || 0,
        compareAtPrice: Number(variant.compare_at_price) || 0,
        image: getVariantImage(variant)
      })).filter(variant => variant.id);
    } catch (error) {
      console.error('Failed to parse mobile variant options:', error);
      return [];
    }
  };

  const getMetaFromForm = (form) => {
    const metaEl = form.querySelector('[data-mobile-variant-meta]');
    if (!metaEl) return {};

    try {
      const meta = JSON.parse(metaEl.textContent || '{}');
      if (!meta || typeof meta !== 'object') return {};
      return meta;
    } catch (error) {
      console.error('Failed to parse mobile variant meta:', error);
      return {};
    }
  };

  const getSalePercentage = (variant) => {
    if (!variant) return 0;
    if (!Number.isFinite(variant.price) || !Number.isFinite(variant.compareAtPrice)) return 0;
    if (variant.compareAtPrice <= variant.price) return 0;
    const difference = variant.compareAtPrice - variant.price;
    const percentage = Math.round((difference / variant.compareAtPrice) * 100);
    if (!Number.isFinite(percentage) || percentage <= 0) return 0;
    return percentage;
  };

  const getBadgeText = (selectedVariant) => {
    const salePercentage = getSalePercentage(selectedVariant);
    if (salePercentage > 0) {
      const discountLabel = String(state.meta.discountOffLabel || 'off').trim();
      return discountLabel ? `${salePercentage}% ${discountLabel}` : `${salePercentage}%`;
    }

    if (!state.meta.hasBadge) return '';
    return String(state.meta.badgeText || '').trim();
  };

  const syncFormVariantFields = () => {
    if (!state.form || !state.selectedVariantId) return;

    if (state.form.elements?.variant) {
      state.form.elements.variant.value = String(state.selectedVariantId);
    }
    if (state.form.elements?.id) {
      state.form.elements.id.value = String(state.selectedVariantId);
    }
  };

  const setPickerLoading = (loading) => {
    if (!addBtnEl) return;
    addBtnEl.classList.toggle(CLASSES.POINTER_EVENTS_NONE, loading);
    addBtnEl.classList.toggle('opacity-60', loading);
  };

  const resetPickerState = () => {
    state.form = null;
    state.variants = [];
    state.selectedVariantId = 0;
    state.meta = {};
  };

  const setPickerOpen = (open) => {
    pickerEl.classList.toggle(CLASSES.POINTER_EVENTS_NONE, !open);
    pickerEl.classList.toggle(CLASSES.OPACITY_0, !open);
    pickerEl.classList.toggle(CLASSES.OPACITY_100, open);
    pickerEl.setAttribute('aria-hidden', String(!open));

    if (backdropEl) {
      backdropEl.classList.toggle(CLASSES.OPACITY_0, !open);
      backdropEl.classList.toggle(CLASSES.OPACITY_100, open);
    }

    if (panelEl) {
      panelEl.classList.toggle('translate-y-full', !open);
      panelEl.classList.toggle('translate-y-0', open);
      panelEl.classList.toggle(CLASSES.OPACITY_0, !open);
      panelEl.classList.toggle(CLASSES.OPACITY_100, open);
    }

    if (open) {
      lockBodyScroll(MOBILE_VARIANT_PICKER.SCROLL_LOCK_TOKEN);
      state.previouslyFocusedEl = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    } else {
      unlockBodyScroll(MOBILE_VARIANT_PICKER.SCROLL_LOCK_TOKEN);
      if (state.previouslyFocusedEl && document.contains(state.previouslyFocusedEl)) {
        state.previouslyFocusedEl.focus();
      }
      state.previouslyFocusedEl = null;
      setPickerLoading(false);
      resetPickerState();
    }
  };

  const cloneSlide = (imageSrc, title, index) => {
    const slide = slideTemplate.content.cloneNode(true).firstElementChild;
    const img = slide.querySelector('[data-mobile-variant-slide-img]');

    img.src = getSizedImageSrc(imageSrc, IMAGES.DEFAULT_WIDTH);
    img.srcset = IMAGES.WIDTHS.map(w => `${getSizedImageSrc(imageSrc, w)} ${w}w`).join(', ');
    img.sizes = IMAGES.SIZES;
    img.alt = index ? `${title} image ${index + 1}` : title;
    img.loading = index === 0 ? 'eager' : 'lazy';

    return slide;
  };

  const renderImages = (title, fallbackImage = '') => {
    if (!imagesWrapEl) return;

    const variantImage = getSelectedVariant()?.image || '';
    const imageList = getUniqueImages([
      variantImage,
      ...(Array.isArray(state.meta.productImages) ? state.meta.productImages : []),
      state.meta.productImage || '',
      fallbackImage
    ]);

    imagesWrapEl.innerHTML = '';
    if (!imageList.length) return;

    const scrollContainer = document.createElement('div');
    scrollContainer.className = 'flex w-full snap-x snap-mandatory overflow-x-auto no-scrollbar';

    imageList.forEach((imageSrc, index) => {
      scrollContainer.appendChild(cloneSlide(imageSrc, title, index));
    });

    imagesWrapEl.appendChild(scrollContainer);
  };

  const cloneSwatch = (variant) => {
    const button = swatchTemplate.content.cloneNode(true).firstElementChild;

    button.textContent = variant.title;
    button.dataset.mobileVariantId = variant.id;

    const isActive = variant.id === state.selectedVariantId;
    button.classList.add(...(isActive ? MOBILE_VARIANT_PICKER.SWATCH_ACTIVE_CLASSES : MOBILE_VARIANT_PICKER.SWATCH_INACTIVE_CLASSES).split(' '));

    if (!variant.available) {
      button.disabled = true;
      button.classList.add(...MOBILE_VARIANT_PICKER.SWATCH_UNAVAILABLE_CLASSES.split(' '));
    }

    return button;
  };

  const renderVariantOptions = () => {
    if (!optionsWrapEl) return;

    const selectedVariant = getSelectedVariant();

    optionsWrapEl.innerHTML = '';
    state.variants.forEach((variant) => {
      optionsWrapEl.appendChild(cloneSwatch(variant));
    });

    if (selectedVariant && priceEl) {
      priceEl.textContent = formatMoney(selectedVariant.price);
    }

    if (comparePriceEl) {
      const showComparePrice = selectedVariant && selectedVariant.compareAtPrice > selectedVariant.price;
      comparePriceEl.textContent = showComparePrice ? formatMoney(selectedVariant.compareAtPrice) : '';
      comparePriceEl.classList.toggle('hidden', !showComparePrice);
    }

    if (saleBadgeEl) {
      const badgeText = getBadgeText(selectedVariant);
      saleBadgeEl.textContent = badgeText;
      saleBadgeEl.classList.toggle('hidden', !badgeText);
      saleBadgeEl.classList.toggle('inline-flex', Boolean(badgeText));
    }

    if (addBtnEl) {
      addBtnEl.disabled = !selectedVariant || !selectedVariant.available;
    }
  };

  // Events
  pickerEl.addEventListener('click', (evt) => {
    const closeBtn = evt.target.closest('[data-mobile-variant-close]');
    if (closeBtn) {
      setPickerOpen(false);
      return;
    }

    const optionBtn = evt.target.closest('[data-mobile-variant-id]');
    if (optionBtn) {
      state.selectedVariantId = Number(optionBtn.dataset.mobileVariantId) || 0;
      syncFormVariantFields();
      renderVariantOptions();
      renderImages(state.meta.productTitle || '');
      return;
    }

    const addBtn = evt.target.closest('[data-mobile-variant-add]');
    if (!addBtn || addBtn.disabled) return;

    (async () => {
      const selectedVariant = getSelectedVariant();
      if (!selectedVariant) return;

      const quantity = Number(state.form?.elements?.quantity?.value || 1);
      setPickerLoading(true);

      try {
        await addToCart(selectedVariant.id, quantity);
        await renderMinicart();
        window.theme.minicart?.open({ announcement: window.theme.strings?.addedToCart });
        setPickerOpen(false);
      } catch (error) {
        console.error('Failed to add variant from mobile picker:', error);
      } finally {
        setPickerLoading(false);
      }
    })();
  });

  document.addEventListener('keydown', (evt) => {
    if (evt.key === 'Escape' && pickerEl.getAttribute('aria-hidden') === 'false') {
      setPickerOpen(false);
    }
  });

  trapFocus(panelEl);

  // Public API
  const openFromForm = (form) => {
    if (window.innerWidth >= BREAKPOINTS.DESKTOP) return false;
    if (!form) return false;

    const variants = getVariantsFromForm(form);
    if (variants.length <= 1) return false;
    const meta = getMetaFromForm(form);

    state.form = form;
    state.variants = variants;
    state.selectedVariantId = Number(form?.elements?.variant?.value || form?.elements?.id?.value || 0) || variants.find(v => v.available)?.id || variants[0].id;
    syncFormVariantFields();

    const productCardEl = form.closest('[data-product-card]');
    const fallbackTitle = productCardEl?.querySelector('[data-product-card-title]')?.textContent?.trim() || '';
    const fallbackImage = productCardEl?.dataset.productCardImage || '';
    const productTitle = meta.productTitle || fallbackTitle;
    const optionName = meta.optionName || 'Select option';
    state.meta = {
      ...meta,
      productTitle,
      optionName,
      productImage: meta.productImage || fallbackImage
    };

    if (titleEl) titleEl.textContent = productTitle;
    if (optionNameEl) optionNameEl.textContent = optionName;

    renderImages(productTitle, fallbackImage);
    renderVariantOptions();
    setPickerLoading(false);
    setPickerOpen(true);

    return true;
  };

  if (!window.theme) window.theme = {};
  window.theme.mobileVariantPicker = { openFromForm };
};

export default initMobileVariantPicker;
